import { supabase } from '@/lib/supabase';

/**
 * 판매자 신청 상태 조회
 */
export async function getApplicationStatus(userId: string): Promise<{
  success: boolean;
  data?: {
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    appliedAt: string;
    processedAt?: string;
    rejectReason?: string;
  };
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('seller_applications')
      .select('status, created_at, processed_at, reject_reason')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // 신청 내역이 없는 경우
        return {
          success: false,
          error: '판매자 신청 내역이 없습니다.'
        };
      }
      throw error;
    }

    return {
      success: true,
      data: {
        status: data.status,
        appliedAt: data.created_at,
        processedAt: data.processed_at,
        rejectReason: data.reject_reason
      }
    };
  } catch (error) {
    console.error('Get application status error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '신청 상태를 조회하는데 실패했습니다'
    };
  }
}

/**
 * 판매자 신청 내역 상세 조회
 */
export async function getApplicationDetails(userId: string): Promise<{
  success: boolean;
  data?: {
    id: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    accountInfo: {
      email: string;
      name: string;
      phone: string;
    };
    businessInfo: {
      companyName: string;
      representative: string;
      businessNumber: string;
      bankName: string;
      accountNumber: string;
      accountHolder: string;
    };
    storeInfo: {
      storeName: string;
      storePhone: string;
      address: string;
      detailAddress?: string;
      foodTypes: string[];
      customFoodType?: string;
      representativeMenus: string[];
      operatingHours?: any;
    };
    agreements: {
      termsAgreed: boolean;
      privacyAgreed: boolean;
      marketingConsent: boolean;
    };
    appliedAt: string;
    processedAt?: string;
    processedBy?: string;
    rejectReason?: string;
  };
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('seller_applications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return {
          success: false,
          error: '판매자 신청 내역이 없습니다.'
        };
      }
      throw error;
    }

    // operating_hours JSON 파싱
    let operatingHours = null;
    if (data.operating_hours) {
      try {
        operatingHours = typeof data.operating_hours === 'string' 
          ? JSON.parse(data.operating_hours) 
          : data.operating_hours;
      } catch (e) {
        console.error('Operating hours parse error:', e);
      }
    }

    return {
      success: true,
      data: {
        id: data.id,
        status: data.status,
        accountInfo: {
          email: data.email,
          name: data.name,
          phone: data.phone
        },
        businessInfo: {
          companyName: data.company_name,
          representative: data.representative,
          businessNumber: data.business_number,
          bankName: data.bank_name,
          accountNumber: data.account_number,
          accountHolder: data.account_holder
        },
        storeInfo: {
          storeName: data.store_name,
          storePhone: data.store_phone,
          address: data.address,
          detailAddress: data.detail_address,
          foodTypes: data.food_types || [],
          customFoodType: data.custom_food_type,
          representativeMenus: data.representative_menus || [],
          operatingHours: operatingHours
        },
        agreements: {
          termsAgreed: data.terms_agreed,
          privacyAgreed: data.privacy_agreed,
          marketingConsent: data.marketing_consent
        },
        appliedAt: data.created_at,
        processedAt: data.processed_at,
        processedBy: data.processed_by,
        rejectReason: data.reject_reason
      }
    };
  } catch (error) {
    console.error('Get application details error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '신청 내역을 조회하는데 실패했습니다'
    };
  }
}